/**
 * Protected Route Component
 * Ensures user is authenticated before rendering children
 */

import { useSession } from 'next-auth/react';
import { Container, Text, Center, Paper, Stack } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { LoginButton } from './LoginButton';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { data: session, status } = useSession();

  if (status === 'loading') {
    return (
      <Container size="sm" py="xl">
        <Center>
          <Text>Loading...</Text>
        </Center>
      </Container>
    );
  }

  if (!session?.user) {
    return fallback || (
      <Container size="sm" py="xl">
        <Center>
          <Paper p="xl" shadow="md" radius="md" w={400}>
            <Stack align="center" gap="lg">
              <IconLock size={48} color="var(--mantine-color-blue-6)" />
              
              <Stack align="center" gap="xs">
                <Text size="xl" fw={600}>
                  Sign in required
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  You need to sign in to access this feature
                </Text>
              </Stack>

              <LoginButton />
            </Stack>
          </Paper>
        </Center>
      </Container>
    );
  }

  return <>{children}</>;
}